import React, { useState, useEffect } from 'react';
import { PWAUtils } from '../../utils/pwa';

interface PWAInstallPromptProps {
  className?: string;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ className = '' }) => {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const checkInstallStatus = () => {
      setCanInstall(PWAUtils.canInstall());
      setIsInstalled(PWAUtils.isAppInstalled());
    };

    checkInstallStatus();

    // Check periodically for changes in install status
    const interval = setInterval(checkInstallStatus, 1000);

    // Show prompt after a delay if app can be installed
    const timer = setTimeout(() => {
      if (PWAUtils.canInstall() && !PWAUtils.isAppInstalled()) {
        setShowPrompt(true);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    const success = await PWAUtils.showInstallPrompt();
    if (success) {
      setShowPrompt(false);
      setCanInstall(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (isInstalled || !canInstall) {
    return null;
  }

  return (
    <>
      {/* Install Button (always visible if can install) */}
      {canInstall && !showPrompt && (
        <button
          onClick={handleInstall}
          className={`bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 font-medium text-sm ${className}`}
        >
          📱 Install App
        </button>
      )}

      {/* Install Prompt Banner */}
      {showPrompt && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg p-4 z-50">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Install BarbrIQ
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Install our app for better experience
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm px-2 py-1"
              >
                Dismiss
              </button>
              <button
                onClick={handleInstall}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm font-medium"
              >
                Install
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;